import React, { useEffect, useState } from 'react';
import useTabQueryParam from '../../hooks/useTabQueryParam';
import { useNavigate } from 'react-router-dom';
import Table from '../../components/Table';
import axios from 'axios';
import { fetchCompanyMembers, fetchCollection, registerMember, updateMember, fetchCompanyMembersWithSearch } from './api';
import { getApiErrorMessage } from '../../utils/apiError';
import { toast } from 'react-toastify';

const roleMap = {
  1: 'Admin',
  2: 'Employee',
  3: 'Manager',
};

// Helper to get reporting manager name by id
const Members = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce searchValue
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 2000);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setLoading(true);
    fetchCompanyMembersWithSearch(debouncedSearch)
      .then(members => {
        setData(members.map(member => ({
          id: member.id,
          name: member.first_name + ' ' + member.last_name,
          email: member.email,
          role: roleMap[member.role_id] || member.role_id,
          role_id: member.role_id,
          status: member.is_active ? 'Active' : 'Inactive',
          is_active: member.is_active,
          reporting_manager_id: member.reporting_manager_id,
          reporting_manager: member.reporting_manager_name || '',
        })));
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch]);

  const openModal = (member = null) => {
    setEditMember(member);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditMember(null);
  };

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useTabQueryParam('tab', 'members');
  const TABS = [
    { key: 'members', label: 'Members', route: '' },
    { key: 'leave-policy', label: 'Leave Policy', route: 'leave-policy' },
    { key: 'holidays', label: 'Holidays', route: 'holidays' },
  ];

  const handleTabClick = (tabKey, route) => {
    setActiveTab(tabKey);
    navigate(`/company/${route}`);
  };

  return (
    <div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Members</h2>
        <input
          type="text"
          placeholder="Search members..."
          className="border px-3 py-2 rounded mr-4 min-w-[200px]"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />
        <button className="bg-indigo-600 text-white px-4 py-2 rounded font-semibold" onClick={() => openModal()}>Add Member</button>
      </div>
      <Table columns={[
        { key: 'name', title: 'Name' },
        { key: 'email', title: 'Email' },
        { key: 'role', title: 'Role' },
        { key: 'status', title: 'Status' },
        { key: 'reporting_manager', title: 'Reporting Manager' }
      ]} data={data} actions={row => (
        <>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded mr-2" title="Edit" onClick={() => openModal(row)}><i className="fas fa-edit" /></button>
          <button className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded" title="Delete"><i className="fas fa-trash" /></button>
        </>
      )} />
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl min-w-[400px] max-w-full w-full sm:w-[500px] flex flex-col h-[92vh]">
            {/* Modal Header */}
            <div className="px-6 pt-6 pb-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editMember ? 'Edit Member' : 'Add Member'}</h3>
              <button className="text-gray-500 hover:text-gray-700 text-xl font-bold" onClick={closeModal} aria-label="Close">&times;</button>
            </div>
            {/* Modal Body (fills available space) */}
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {editMember ? (
                <EditMemberForm member={editMember} onClose={closeModal} onUpdated={() => {
                  closeModal();
                  setLoading(true);
                  fetchCompanyMembers().then(data => {
                    setData(data.map(member => ({
                      id: member.id,
                      name: member.first_name + ' ' + member.last_name,
                      email: member.email,
                      role: roleMap[member.role_id] || member.role_id,
                      role_id: member.role_id,
                      status: member.is_active ? 'Active' : 'Inactive',
                      is_active: member.is_active,
                      reporting_manager_id: member.reporting_manager_id,
                      reporting_manager: member.reporting_manager_name || '',
                    })));
                  }).finally(() => setLoading(false));
                }} />
              ) : (
                <AddMemberForm onClose={closeModal} onCreated={() => {
                  closeModal();
                  setLoading(true);
                  fetchCompanyMembers().then(data => {
                    setData(data.map(member => ({
                      id: member.id,
                      name: member.first_name + ' ' + member.last_name,
                      email: member.email,
                      role: roleMap[member.role_id] || member.role_id,
                      role_id: member.role_id,
                      status: member.is_active ? 'Active' : 'Inactive',
                      is_active: member.is_active,
                      reporting_manager_id: member.reporting_manager_id,
                      reporting_manager: member.reporting_manager_name || '',
                    })));
                  }).finally(() => setLoading(false));
                }} />
              )}
            </div>
            {/* Modal Footer (empty for now, can add actions if needed) */}
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50"></div>
          </div>
        </div>
      )}
      {loading && <div className="text-center py-8">Loading...</div>}
    </div>
  );
};

// --- EditMemberForm component ---

function EditMemberForm({ member, onClose, onUpdated }) {
  const [roleId, setRoleId] = React.useState(member.role_id);
  const [reportingManagerId, setReportingManagerId] = React.useState('');
  const [roles, setRoles] = React.useState([]);
  const [managers, setManagers] = React.useState([]);
  const [loadingCollections, setLoadingCollections] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState(member.is_active === false ? 'Inactive' : 'Active');
  console.log(member);
  
  // Set the initial value for reportingManagerId when managers and member are loaded
  React.useEffect(() => {
    if (member.reporting_manager_id) {
      setReportingManagerId(String(member.reporting_manager_id));
    } else {
      setReportingManagerId('');
    }
  }, [member.reporting_manager_id, managers.length]);

  // React.useEffect(() => {
  //   setLoadingCollections(true);
  //   axios.get('http://127.0.0.1:8000/api/accounts/collection/?type=role,reporting_manager', {
  //     headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
  //   })
  //     .then(res => {
  //       console.log(res);
        
  //       setRoles(res.role || []);
  //       setManagers(res.reporting_manager || []);
  //       console.log(roles, roleId, member, reportingManagerId);
        
  //     })
  //     .catch(() => {
  //       setRoles([]);
  //       setManagers([]);
  //     })
  //     .finally(() => setLoadingCollections(false));
  // }, []);

  React.useEffect(() => {
    setLoadingCollections(true);
    fetchCollection()
    .then(res => {
      setRoles(res.role || []);
      setManagers(res.reporting_manager || []);
    })
    .catch(() => {
      setRoles([]);
      setManagers([]);
    })
    .finally(() => setLoadingCollections(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await updateMember(member.id, {
        role_id: Number(roleId),
        reporting_manager_id: reportingManagerId ? Number(reportingManagerId) : null,
        is_active: status === 'Active',
      });
      onUpdated();
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Role</label>
          <select
            className="border rounded px-3 py-2 w-full"
            value={roleId}
            onChange={e => setRoleId(e.target.value)}
            required
            disabled={loadingCollections}
          >
            {loadingCollections ? (
              <option>Loading...</option>
            ) : (
              roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))
            )}
          </select>
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Reporting Manager</label>
          <select
            className="border rounded px-3 py-2 w-full"
            value={reportingManagerId}
            onChange={e => setReportingManagerId(e.target.value)}
            disabled={loadingCollections}
          >
            <option value="">-- None --</option>
            {loadingCollections ? (
              <option>Loading...</option>
            ) : (
              managers.map(manager => (
                <option key={manager.id} value={String(manager.id)}>{manager.name}</option>
              ))
            )}
          </select>
        </div>
      </div>
      {/* Status Dropdown */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Status</label>
        <select
          className="border rounded px-3 py-2 w-full"
          value={status}
          onChange={e => setStatus(e.target.value)}
          disabled={submitting}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>
      {/* Error is now shown as toast, not inline */}
      <div className="flex flex-row-reverse gap-4 mt-6">
        <button
          type="submit"
          className="bg-indigo-600 text-white px-4 py-2 rounded font-semibold"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          className="bg-indigo-500 text-white px-4 py-2 rounded font-semibold"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </form>
  );
}

// --- AddMemberForm component ---
function AddMemberForm({ onClose, onCreated }) {
  const [form, setForm] = React.useState({
    username: '',
    email: '',
    password: '',
    company: 1,
    emp_code: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    reporting_manager: '',
    joining_date: '',
    role: ''
  });
  const [roles, setRoles] = React.useState([]);
  const [managers, setManagers] = React.useState([]);
  const [loadingCollections, setLoadingCollections] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    setLoadingCollections(true);
  fetchCollection()
    .then(res => {
      setRoles(res.role || []);
      setManagers(res.reporting_manager || []);
    })
    .catch(() => {
      setRoles([]);
      setManagers([]);
    })
    .finally(() => setLoadingCollections(false));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerMember({
        ...form,
        username: form.email, // username is same as email
        reporting_manager: form.reporting_manager ? Number(form.reporting_manager) : undefined,
        role: form.role ? Number(form.role) : undefined,
        emp_code: form.emp_code || undefined, // optional
        joining_date: form.joining_date || undefined, // optional
        phone_number: form.phone_number || undefined,
      });
      toast.success('Member created successfully!');
      onCreated();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Email</label>
          <input type="email" name="email" maxLength={50} className="border rounded px-3 py-2 w-full" value={form.email} onChange={handleChange} required />
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Phone Number</label>
          <input type="tel" name="phone_number" maxLength={10} pattern="\d{10}" className="border rounded px-3 py-2 w-full" value={form.phone_number} onChange={handleChange} required />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">First Name</label>
          <input type="text" name="first_name" maxLength={50} className="border rounded px-3 py-2 w-full" value={form.first_name} onChange={handleChange} required />
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Last Name</label>
          <input type="text" name="last_name" maxLength={50} className="border rounded px-3 py-2 w-full" value={form.last_name} onChange={handleChange} required />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Password</label>
          <input type="password" name="password" className="border rounded px-3 py-2 w-full" value={form.password} onChange={handleChange} required />
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Joining Date</label>
          <input type="date" name="joining_date" className="border rounded px-3 py-2 w-full" value={form.joining_date} onChange={handleChange} />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Reporting Manager</label>
          <select name="reporting_manager" className="border rounded px-3 py-2 w-full" value={form.reporting_manager} onChange={handleChange} disabled={loadingCollections}>
            <option value="">-- None --</option>
            {loadingCollections ? <option>Loading...</option> : managers.map(manager => (
              <option key={manager.id} value={manager.id}>{manager.name}</option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block font-medium mb-1">Role</label>
          <select name="role" className="border rounded px-3 py-2 w-full" value={form.role} onChange={handleChange} required disabled={loadingCollections}>
            <option value="">Select Role</option>
            {loadingCollections ? <option>Loading...</option> : roles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
        </div>
        
      </div>
      <div className="mb-3">
        <label className="block font-medium mb-1">Employee Code (optional)</label>
        <input type="text" name="emp_code" className="border rounded px-3 py-2 w-full" value={form.emp_code} onChange={handleChange} maxLength={20} />
      </div>
      <div className="flex flex-row-reverse gap-4 mt-6">
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded font-semibold min-w-[90px] flex items-center justify-center"
          disabled={submitting}
        >
          {submitting ? <span className="loader mr-2"></span> : null} Create
        </button>
        <button
          type="button"
          className="bg-gray-500 text-white px-4 py-2 rounded font-semibold"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
      {/* Spinner CSS */}
      <style>{`.loader { border: 2px solid #f3f3f3; border-top: 2px solid #fff; border-right: 2px solid #fff; border-radius: 50%; width: 16px; height: 16px; animation: spin 0.8s linear infinite; display: inline-block; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </form>
  );
}

export default Members;
